import { ViewDensity } from "@common/types/views/views";
import { DENSITY_ROW_HEIGHT } from "@constants";
import { useViewTableKeyboardShortcuts } from "@design-system/ViewTable/hooks/useViewTableKeyboardShortcuts";
import { useDynamicTopOffset } from "@design-system/hooks/useDynamicTopOffset";
import { assignRef } from "@design-system/hooks/assignRef";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import {
    useTable,
    type RowSelectionState,
    type ExpandedState,
} from "@tanstack/react-table";
import {
    viewTableFeatures,
    type ViewTableReactInstance,
    type ViewTableRow,
} from "./core/table";
import type { Virtualizer, VirtualItem } from "@tanstack/react-virtual";
import { useRowVirtualizer } from "@design-system/DataGrid/grid/useRowVirtualizer";
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragStartEvent,
    type DragMoveEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import classNames from "classnames";
import {
    useCallback,
    useId,
    useMemo,
    useRef,
    useState,
    useEffect,
    useInsertionEffect,
    useLayoutEffect,
    memo,
    useImperativeHandle,
    MouseEvent,
} from "react";
// oxlint-disable-next-line no-restricted-imports -- ViewTable owns the GridTable implementation.
import {
    GridTable,
    GridTableHeader,
} from "@design-system/Tables/GridTable/GridTable";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ColumnHeaderVariantAction } from "@lib/views";
import { Loader } from "@design-system/Loader/Loader";
import { Button } from "@design-system/Button/Button";
import { Menu } from "@design-system/Menu/Menu.lib";
import { DotsHorizontalIcon } from "@icons";
import { styleWithVars } from "@lib/cssVars";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import styles from "./ViewTable.module.scss";
import { useAutoExpand } from "./core/useAutoExpand";
import { useSelectColumnSize } from "./core/useSelectColumnSize";
import {
    calculateInitialVisibleRows,
    HEADER_ROW_PX,
} from "./core/calculateInitialVisibleRows";
import { buildColumns } from "./core/buildColumns";
import { getPageRowSelection } from "./core/selectAll";
import { getRowRenderState } from "./core/getRowRenderState";
import { getColumnPinningData } from "./core/getColumnPinningData";
import { ViewTableLoadMoreRows } from "./ViewTableLoadMore";
import { VIEW_TABLE_SCROLL_ROOT_ATTRIBUTE } from "./viewTableScrollRoot";
import { getGridTemplateColumns } from "./core/getGridTemplateColumns";
import { useColumnResizeSync } from "./core/useColumnResizeSync";

import {
    BLOCK_EVENT_KEY,
    buildShiftClickSelection,
    findNextFocusableRowIndex,
} from "./ViewTable.lib";
import {
    buildDropIndicatorState,
    getActiveDragSetIds,
    pointerWithinThenClosestCenter,
    resolveDropCommitTarget,
    resolveRowDropFromEvent,
    type DropIndicatorState,
    type ResolvedRowDrop,
} from "./ViewTable.dnd";
import { ViewTableRowComponent, SortableViewTableRow } from "./ViewTableRow";
import { ViewTableDragOutRow } from "./ViewTableDragOutRow";
import { RowDragOutProvider } from "./rowDragOut";
import { useKeyboardShortcutOwner } from "./core/useKeyboardShortcutOwner";
import type {
    ScrollAlign,
    ViewColumnWithVariantOptions,
    ViewTableProps,
    ViewTableRowClickEvent,
} from "./ViewTable.types";
import {
    renderFullWidthRowIfPresent,
    renderViewTableHeaderCell,
} from "./viewTableRenderers";
export type { ViewTableRowDragOut } from "./rowDragOut";
export type { CellContextWithDragHandle } from "./ViewTableRow";
export type { ViewTableHandle } from "./ViewTable.types";

// Called during ViewTable's render, not rendered as components. A capitalized
// name makes the React Compiler memoize them as components, and two of them then
// share ViewTable's memo cache, so the empty state shows the cached loader.
const renderDefaultEmptyView = () => <EmptyState header="No data available" />;
const renderDefaultLoadingView = () => <Loader />;

function noop(): void {}

const ViewTableInner = <
    TData extends { id: number | string },
    TColumn extends string,
    TSort extends string,
>({
    data,
    view,
    config,
    selectedRowId,
    onRowClick,
    onPreviewFocused,
    focusedRowIndex: controlledFocusedRowIndex,
    onFocusedRowChange,
    rowSelection: controlledRowSelection,
    onRowSelectionChange: controlledOnRowSelectionChange,
    selectAllControl,
    enableSubRowSelection,
    sort,
    onSortChange,
    onSortRemove,
    isSortLoading,
    emptyView = renderDefaultEmptyView,
    loadingView = renderDefaultLoadingView,
    tableMeta,
    enableRowSelection,
    className,
    getRowClassName,
    footer,
    prependContent,
    appendContent,
    style,
    rowBorders = true,
    columnBorders = false,
    hideTopBorder = false,
    expanded: controlledExpanded,
    onExpandedChange: controlledOnExpandedChange,
    enableKeyboardShortcuts = false,
    isEnterSelectionDisabled = false,
    fixedTopOffsetPx: fixedTopOffsetPxOverride,
    recomputeTopOffsetOn,
    autoExpandOnMount = false,
    isParentSelectable = false,
    onViewChange,
    centerColumns,
    enableColumnResizing = true,
    viewChangeDebounceMs = 50,
    getRowId: customGetRowId,
    renderFullWidthRow,
    outerBorder: bordered = false,
    scrollContainerRef,
    onScrollContainerMount,
    initialVisibleRows,
    nestingIndentPx,
    rowDragAndDrop,
    rowDragOut,
    loadMore,
    ref,
}: Readonly<ViewTableProps<TData, TColumn, TSort>>) => {
    const tableContainerRef = useRef<HTMLDivElement>(null);
    // Keep the DOM callback ref referentially stable. An inline
    // `ref={(el) => ...}` identity change makes React call the old callback
    // with `null` and the new one with the node on every render; when
    // `onScrollContainerMount` is a setState (e.g. document-select infinite
    // scroll), that loops into "Maximum update depth exceeded" and blanks the
    // screen (KICK-FRONTEND-2T9). `scrollContainerRef` is a ref object, stable
    // across renders.
    const onScrollContainerMountRef = useLatestRef(onScrollContainerMount);
    const assignTableContainerRef = useCallback(
        (el: HTMLDivElement | null) => {
            mergeRefs([tableContainerRef, scrollContainerRef])(el);
            onScrollContainerMountRef.current?.(el);
        },
        [scrollContainerRef, onScrollContainerMountRef],
    );
    // Always call the hook (hooks can't be conditional) so a caller can flip
    // `fixedTopOffsetPx` between `true` and a number across renders; its
    // result is only used below when auto-measurement is actually opted into.
    const { topOffset: measuredTopOffsetPx } = useDynamicTopOffset({
        containerRef: tableContainerRef,
        recomputeOn: recomputeTopOffsetOn,
    });
    const fixedTopOffsetPx =
        fixedTopOffsetPxOverride === true
            ? measuredTopOffsetPx
            : fixedTopOffsetPxOverride;
    // Ref so that scrollToFocusedRow (defined before useVirtualizer) can call
    // rowVirtualizer.scrollToIndex without a dependency cycle.
    const rowVirtualizerRef = useRef<Virtualizer<
        HTMLDivElement,
        Element
    > | null>(null);
    const tableId = useId();
    const renderFullWidthRowRef = useLatestRef(renderFullWidthRow);
    const {
        isKeyboardShortcutsEnabledForTable,
        activateKeyboardShortcutsForTable,
    } = useKeyboardShortcutOwner({ tableId, enableKeyboardShortcuts });

    // Handle scroll position for shadow effects using CSS classes
    // Use requestAnimationFrame for better performance
    const rafIdRef = useRef<number | null>(null);

    // Refs for the focused-row indicator overlay (positioned relative to the
    // wrapper, outside the scroll container). Updated imperatively on scroll
    // so we don't trigger React re-renders for every scroll frame.
    const focusedRowIndicatorRef = useRef<HTMLDivElement | null>(null);
    const focusedVirtualItemRef = useRef<VirtualItem | undefined>(undefined);

    // Track if we're in keyboard navigation mode to temporarily disable hover
    const isKeyboardNavigatingRef = useRef(false);

    const updateScrollClasses = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container) {
            return;
        }

        const { scrollLeft, scrollWidth, clientWidth } = container;
        const maxScroll = scrollWidth - clientWidth;
        const hasOverflow = scrollWidth > clientWidth;

        const overflowLeft = scrollLeft > 0;
        const overflowRight = scrollLeft < maxScroll - 1; // the -1 is for the scroll, as it never reaches the maxScroll, but it is less than a 1px difference

        container.classList.toggle("overflow-left", overflowLeft);
        container.classList.toggle("overflow-right", overflowRight);
        container.classList.toggle("has-overflow", hasOverflow);

        // Imperatively reposition the indicator so it tracks the focused row
        // during scroll without triggering a React re-render every frame.
        // Strip the keyboard-move transition so scroll tracking stays 1:1.
        const indicator = focusedRowIndicatorRef.current;
        const virtualItem = focusedVirtualItemRef.current;
        if (indicator && virtualItem) {
            indicator.classList.remove(styles.animatePosition);
            indicator.style.top = `${virtualItem.start - container.scrollTop}px`;
        }
    }, []);

    const throttledUpdateScrollClasses = useCallback(() => {
        if (rafIdRef.current) {
            cancelAnimationFrame(rafIdRef.current);
        }
        rafIdRef.current = requestAnimationFrame(updateScrollClasses);
    }, [updateScrollClasses]);

    // Set up scroll listener and initial state
    useEffect(() => {
        const container = tableContainerRef.current;
        if (!container) {
            return;
        }

        // Initial check
        updateScrollClasses();

        // Add throttled scroll listener for better performance
        container.addEventListener("scroll", throttledUpdateScrollClasses, {
            passive: true,
        });

        // Also listen for resize events that might change scroll dimensions
        const resizeObserver = new ResizeObserver(throttledUpdateScrollClasses);
        resizeObserver.observe(container);
        return () => {
            container.removeEventListener(
                "scroll",
                throttledUpdateScrollClasses,
            );
            resizeObserver.disconnect();
            // Cancel any pending RAF
            if (rafIdRef.current) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, [updateScrollClasses, throttledUpdateScrollClasses]);

    const [uncontrolledRowSelection, setUncontrolledRowSelection] =
        useState<RowSelectionState>({});
    const rowSelection = controlledRowSelection ?? uncontrolledRowSelection;
    const setRowSelection =
        controlledOnRowSelectionChange ?? setUncontrolledRowSelection;

    const [uncontrolledExpanded, setUncontrolledExpanded] =
        useState<ExpandedState>({});
    const expanded = controlledExpanded ?? uncontrolledExpanded;
    const setExpanded = controlledOnExpandedChange ?? setUncontrolledExpanded;

    const isRowDragAndDropEnabled =
        !!rowDragAndDrop?.onRowDrop && !rowDragAndDrop.disabled;
    // Both use the row's pointer drag, so reordering turns drag-out off.
    const activeRowDragOut = isRowDragAndDropEnabled ? undefined : rowDragOut;
    const NonSortableRow = activeRowDragOut
        ? ViewTableDragOutRow
        : ViewTableRowComponent;
    const selectColumnSize = useSelectColumnSize({
        containerRef: tableContainerRef,
        selectCellClassName: styles.selectColumn,
        hasSelectColumn: view.options?.bulkActions === true,
    });

    const { columns, columnPinning } = useMemo(
        () =>
            buildColumns({
                view,
                config,
                sort,
                onSortChange,
                onSortRemove,
                isSortLoading,
                selectAllControl,
                centerColumns,
                selectColumnSize,
            }),
        [
            view,
            config,
            sort,
            onSortChange,
            onSortRemove,
            isSortLoading,
            selectAllControl,
            centerColumns,
            selectColumnSize,
        ],
    );

    const tableEnableRowSelection = useMemo(() => {
        if (typeof enableRowSelection === "function") {
            return (row: ViewTableRow<TData>) =>
                enableRowSelection(row.original);
        }
        return enableRowSelection ?? !!view.options?.bulkActions;
    }, [enableRowSelection, view.options?.bulkActions]);

    // passing data = null caused the expresion data ?? [] passed to useTable to be re-evaluated on every render
    const refStableData = useMemo(() => data ?? [], [data]);

    // Focus state management (unified for mouse hover and keyboard navigation)
    const [uncontrolledFocusedRowIndex, setUncontrolledFocusedRowIndex] =
        useState<number>(-1);

    const focusedRowIndex =
        controlledFocusedRowIndex ?? uncontrolledFocusedRowIndex;

    // Refs for values read by keyboard callbacks — avoids recreating
    // ~10 useCallbacks every time focusedRowIndex or table changes.
    const focusedRowIndexRef = useLatestRef(focusedRowIndex);

    // Tracks the identity (row id) of the focused/clicked row so its index can
    // be re-derived after the flattened row list changes shape (rows inserted
    // or removed by expanding/collapsing a nested group elsewhere in the
    // table). Without this, the focus indicator stays pinned to a stale
    // numeric index and drifts onto a different row than the one still
    // highlighted as selected.
    const focusedRowIdRef = useRef<string | null>(null);

    const setFocusedRowIndex = useCallback(
        (index: number, source: "keyboard" | "click") => {
            const focusedRow =
                index >= 0
                    ? (tableRef.current.getRowModel().rows[index] ?? null)
                    : null;
            focusedRowIdRef.current = focusedRow
                ? String(focusedRow.original.id)
                : null;

            // Keep internal focus state in sync even when a listener is provided.
            // Without this, providing onFocusedRowChange can suppress focused-row
            // visuals when focusedRowIndex is uncontrolled.
            setUncontrolledFocusedRowIndex(index);
            onFocusedRowChange?.(
                index >= 0 ? index : null,
                focusedRow?.original ?? null,
                source,
            );
        },
        [onFocusedRowChange],
    );

    // Scroll to focused row when using keyboard navigation
    const scrollToFocusedRow = useCallback((index: number) => {
        // Delegate scroll-into-view to the virtualizer so it works for rows
        // that are currently off-screen (not in the DOM). The virtualizer
        // will render them and scroll the container in one pass.
        rowVirtualizerRef.current?.scrollToIndex(index, {
            behavior: "auto",
            align: "auto", // only scrolls if the row is outside the viewport
        });
    }, []);

    const lastSelectedRowIdRef = useRef<string | null>(null);

    const hasSelectedRows =
        rowSelection && Object.values(rowSelection).some(Boolean);
    useEffect(() => {
        if (!hasSelectedRows) {
            lastSelectedRowIdRef.current = null;
        }
    }, [hasSelectedRows]);

    // tableRef is populated by the insertion effect right after useTable
    // below. useColumnResizeSync and the callbacks here read it lazily, all
    // after that insertion effect. The null-cast initialization keeps the ref
    // typed as non-null so the many `tableRef.current.xxx` call sites don't
    // need null guards.
    const tableRef = useRef<ViewTableReactInstance<TData>>(
        null as unknown as ViewTableReactInstance<TData>,
    );

    const { columnSizing, onColumnSizingChange, userResizedColumnIds } =
        useColumnResizeSync<TData>({
            tableRef,
            tableContainerRef,
            view,
            onViewChange,
            viewChangeDebounceMs,
            enableColumnResizing,
            columnsLength: columns.length,
        });

    // Inline literals on `meta:` would trigger excess property checking against
    // the augmented `TableMeta<TData>`; assigning to a const first widens the
    // type so structural compat (which permits extra fields) is used instead.
    const meta = {
        options: view.options,
        ...tableMeta,
        lastSelectedRowIdRef,
    };

    const table = useTable({
        features: viewTableFeatures,
        data: refStableData,
        columns,
        getSubRows: config.nesting?.enabled
            ? (row: TData) => {
                  const children =
                      row[config.nesting!.childrenKey as keyof TData];
                  return Array.isArray(children) ? children : [];
              }
            : undefined,
        state: {
            columnPinning,
            rowSelection,
            expanded: config.nesting?.enabled ? expanded : undefined,
            ...(enableColumnResizing ? { columnSizing } : {}),
        },
        enableColumnPinning: true,
        enableRowSelection: tableEnableRowSelection,
        enableSubRowSelection:
            tableMeta?.parentSelection === "self"
                ? false
                : enableSubRowSelection,
        enableRowRangeSelection: false,
        enableExpanding: config.nesting?.enabled,
        autoResetExpanded: false,
        onRowSelectionChange: setRowSelection,
        onExpandedChange: config.nesting?.enabled ? setExpanded : undefined,
        ...(enableColumnResizing && { onColumnSizingChange }),
        meta,
        enableColumnResizing,
        getRowId: (row: TData) =>
            customGetRowId ? customGetRowId(row) : String(row.id),
        ...(enableColumnResizing && { columnResizeMode: "onChange" }),
    });

    // Must be an insertion effect: useColumnResizeSync's mount layout effect,
    // declared above, reads the table, and a layout effect here would run after it.
    useInsertionEffect(() => {
        assignRef(tableRef, table);
    });
    const [activeDragRowId, setActiveDragRowId] = useState<string | null>(null);
    // The row currently under the pointer during a drag, plus where the drop
    // would land relative to it — drives the insertion-line / nest-highlight.
    // `isBlocked` is set when the hovered row cannot host the drop (a non-leaf
    // structural row per `canDropOnRow`) or the consumer's `validateDrop`
    // rejects it, so the indicator + cursor can show the drop won't land.
    const [dropIndicator, setDropIndicator] =
        useState<DropIndicatorState | null>(null);
    // Mirror of `dropIndicator` so the (rarely-recreated) drag-end handler can
    // read the latest shown indicator without depending on per-frame state.
    const dropIndicatorRef = useLatestRef(dropIndicator);
    const rowDragSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: rowDragAndDrop?.activationDistance ?? 8,
            },
        }),
    );

    useImperativeHandle(
        ref,
        () => ({
            scrollTo: (options: ScrollToOptions) => {
                tableContainerRef.current?.scrollTo(options);
            },
            scrollToRowIndex: (
                index: number,
                options?: { align?: ScrollAlign },
            ) => {
                rowVirtualizerRef.current?.scrollToIndex(index, {
                    behavior: "auto",
                    align: options?.align ?? "center",
                });
            },
            scrollToRow: (rowId: string, options?: { align?: ScrollAlign }) => {
                const index = table
                    .getRowModel()
                    .rows.findIndex((row) => row.id === rowId);
                if (index < 0) {
                    return;
                }
                rowVirtualizerRef.current?.scrollToIndex(index, {
                    behavior: "auto",
                    align: options?.align ?? "auto",
                });
            },
            expandAll: () => {
                const expandedState: Record<string, boolean> = {};
                table.getCoreRowModel().flatRows.forEach((row) => {
                    if (row.getCanExpand()) {
                        expandedState[String(row.id)] = true;
                    }
                });
                setExpanded(expandedState);
            },
            collapseAll: () => {
                setExpanded({});
            },
            expandRowAndAncestors: (rowId: string) => {
                const row = table
                    .getCoreRowModel()
                    .flatRows.find((flatRow) => flatRow.id === rowId);
                if (!row) {
                    return;
                }
                const currentExpanded = table.state.expanded;
                if (currentExpanded === true) {
                    return;
                }
                const expandedState: Record<string, boolean> = {
                    ...currentExpanded,
                };
                expandedState[row.id] = true;
                row.getParentRows().forEach((parentRow) => {
                    expandedState[parentRow.id] = true;
                });
                setExpanded(expandedState);
            },
        }),
        [table, setExpanded],
    );

    // Auto-expand rows on mount and when new groups appear
    useAutoExpand({
        enabled: autoExpandOnMount && !!config.nesting?.enabled,
        table,
        data: refStableData,
        expanded,
        setExpanded,
        isControlled: !!controlledExpanded,
    });

    // Helper to mark keyboard navigation and disable hover until mouse moves.
    // Also enables the focus-indicator position transition for this move only;
    // scroll handlers strip `.animatePosition` so wheel/trackpad stays instant.
    const markKeyboardNavigation = useCallback(() => {
        isKeyboardNavigatingRef.current = true;
        focusedRowIndicatorRef.current?.classList.add(styles.animatePosition);
    }, []);

    // Re-enable hover when mouse moves after keyboard navigation
    const handleMouseMove = useCallback(() => {
        if (isKeyboardNavigatingRef.current) {
            isKeyboardNavigatingRef.current = false;
        }
    }, []);

    // Add mouse move listener to re-enable hover after keyboard navigation
    useEffect(() => {
        if (!isKeyboardShortcutsEnabledForTable) {
            return;
        }
        const container = tableContainerRef.current;
        if (!container) {
            return;
        }

        container.addEventListener("mousemove", handleMouseMove, {
            passive: true,
        });

        return () => {
            container.removeEventListener("mousemove", handleMouseMove);
        };
    }, [handleMouseMove, isKeyboardShortcutsEnabledForTable]);

    // Keyboard navigation callbacks — use refs for focusedRowIndex and table
    // so these callbacks are stable and don't recreate on every focus change.
    const isFullWidthRowAtIndex = useCallback(
        (index: number) => {
            const row = tableRef.current.getRowModel().rows[index];
            if (!row) {
                return false;
            }
            return renderFullWidthRowRef.current?.(row.original) != null;
        },
        [renderFullWidthRowRef],
    );

    const focusRowByKeyboard = useCallback(
        (nextIndex: number) => {
            if (nextIndex < 0) {
                return;
            }
            setFocusedRowIndex(nextIndex, "keyboard");
            scrollToFocusedRow(nextIndex);
        },
        [setFocusedRowIndex, scrollToFocusedRow],
    );

    const handleFocusNext = useCallback(() => {
        markKeyboardNavigation();
        const visibleRowCount = tableRef.current.getRowModel().rows.length;
        focusRowByKeyboard(
            findNextFocusableRowIndex({
                fromIndex: focusedRowIndexRef.current,
                direction: 1,
                rowCount: visibleRowCount,
                isSkippable: isFullWidthRowAtIndex,
            }),
        );
    }, [
        focusRowByKeyboard,
        isFullWidthRowAtIndex,
        markKeyboardNavigation,
        focusedRowIndexRef,
    ]);

    const handleFocusPrevious = useCallback(() => {
        markKeyboardNavigation();
        const visibleRowCount = tableRef.current.getRowModel().rows.length;
        focusRowByKeyboard(
            findNextFocusableRowIndex({
                fromIndex: focusedRowIndexRef.current,
                direction: -1,
                rowCount: visibleRowCount,
                isSkippable: isFullWidthRowAtIndex,
            }),
        );
    }, [
        focusRowByKeyboard,
        isFullWidthRowAtIndex,
        markKeyboardNavigation,
        focusedRowIndexRef,
    ]);

    const handleSelectFocused = useCallback(() => {
        const visibleRows = tableRef.current.getRowModel().rows;
        const idx = focusedRowIndexRef.current;
        if (idx >= 0 && idx < visibleRows.length && onRowClick) {
            const tableRow = visibleRows[idx];

            if (!isParentSelectable) {
                if (tableRow.getCanExpand()) {
                    tableRow.toggleExpanded();
                    return;
                }
            }

            const row = tableRow.original;
            onRowClick(row);
        }
    }, [onRowClick, isParentSelectable, focusedRowIndexRef]);

    const handlePreviewFocused = useCallback(() => {
        if (!onPreviewFocused) {
            return;
        }
        const visibleRows = tableRef.current.getRowModel().rows;
        const idx = focusedRowIndexRef.current;
        if (idx < 0 || idx >= visibleRows.length) {
            return;
        }
        onPreviewFocused(visibleRows[idx].original);
    }, [focusedRowIndexRef, onPreviewFocused]);

    const handleToggleBulkSelectFocused = useCallback(() => {
        const visibleRows = tableRef.current.getRowModel().rows;
        const idx = focusedRowIndexRef.current;
        if (idx >= 0 && idx < visibleRows.length) {
            const tableRow = visibleRows[idx];
            if (tableRow.getCanSelect()) {
                tableRow.toggleSelected(!tableRow.getIsSelected());
            }
        }
    }, [focusedRowIndexRef]);

    const handleToggleBulkSelectAll = useCallback(() => {
        const isAllPageRowsSelected =
            tableRef.current.getIsAllPageRowsSelected();
        tableRef.current.toggleAllPageRowsSelected(!isAllPageRowsSelected);
    }, []);

    // Shift + Arrow handlers for bulk selection
    const handleShiftSelectNext = useCallback(() => {
        markKeyboardNavigation();
        const visibleRows = tableRef.current.getRowModel().rows;
        const idx = focusedRowIndexRef.current;
        if (idx >= 0 && idx < visibleRows.length) {
            const currentRow = visibleRows[idx];
            if (currentRow.getCanSelect()) {
                currentRow.toggleSelected(true);
            }

            focusRowByKeyboard(
                findNextFocusableRowIndex({
                    fromIndex: idx,
                    direction: 1,
                    rowCount: visibleRows.length,
                    isSkippable: isFullWidthRowAtIndex,
                }),
            );
        }
    }, [
        focusRowByKeyboard,
        isFullWidthRowAtIndex,
        markKeyboardNavigation,
        focusedRowIndexRef,
    ]);

    const handleShiftSelectPrevious = useCallback(() => {
        markKeyboardNavigation();
        const visibleRows = tableRef.current.getRowModel().rows;
        const idx = focusedRowIndexRef.current;
        if (idx >= 0 && idx < visibleRows.length) {
            const currentRow = visibleRows[idx];
            if (currentRow.getCanSelect()) {
                currentRow.toggleSelected(true);
            }

            focusRowByKeyboard(
                findNextFocusableRowIndex({
                    fromIndex: idx,
                    direction: -1,
                    rowCount: visibleRows.length,
                    isSkippable: isFullWidthRowAtIndex,
                }),
            );
        }
    }, [
        focusRowByKeyboard,
        isFullWidthRowAtIndex,
        markKeyboardNavigation,
        focusedRowIndexRef,
    ]);

    // CMD/Ctrl + Up/Down handlers for jumping to top/bottom
    const handleJumpToTop = useCallback(() => {
        markKeyboardNavigation();
        const visibleRowCount = tableRef.current.getRowModel().rows.length;
        focusRowByKeyboard(
            findNextFocusableRowIndex({
                fromIndex: -1,
                direction: 1,
                rowCount: visibleRowCount,
                isSkippable: isFullWidthRowAtIndex,
            }),
        );
    }, [focusRowByKeyboard, isFullWidthRowAtIndex, markKeyboardNavigation]);

    const handleJumpToBottom = useCallback(() => {
        markKeyboardNavigation();
        const visibleRowCount = tableRef.current.getRowModel().rows.length;
        focusRowByKeyboard(
            findNextFocusableRowIndex({
                fromIndex: visibleRowCount,
                direction: -1,
                rowCount: visibleRowCount,
                isSkippable: isFullWidthRowAtIndex,
            }),
        );
    }, [focusRowByKeyboard, isFullWidthRowAtIndex, markKeyboardNavigation]);

    // Hover is handled entirely via CSS :hover pseudo-class on .row
    // to avoid re-rendering all rows on every mouse movement.

    const handleDeselectAll = useCallback(() => {
        onRowClick?.(undefined);
    }, [onRowClick]);

    // Cache the row model to avoid repeated TanStack cache lookups.
    // getRowModel().rows is already flat-expanded: parent rows are followed
    // by their children inline, so the virtualizer just indexes into this array.
    const rows = table.getRowModel().rows;
    const rowIdToIndexMap = useMemo(
        () =>
            new Map(rows.map((row, index) => [String(row.original.id), index])),
        [rows],
    );

    // Re-derive the focused row's index whenever the flattened row list
    // changes shape (e.g. a sibling group above it expands/collapses), so the
    // focus indicator stays attached to the same row instead of drifting onto
    // whatever row now occupies the old numeric index. Runs before paint to
    // avoid a visible flash of the indicator over the wrong row.
    useLayoutEffect(() => {
        const focusedRowId = focusedRowIdRef.current;
        if (focusedRowId === null) {
            return;
        }
        const currentIndex = rowIdToIndexMap.get(focusedRowId) ?? -1;
        setUncontrolledFocusedRowIndex((prevIndex) =>
            prevIndex === currentIndex ? prevIndex : currentIndex,
        );
    }, [rowIdToIndexMap]);

    const rowByIdMap = useMemo(
        () =>
            new Map(rows.map((row) => [String(row.original.id), row.original])),
        [rows],
    );
    // Opt-in positional drop: drives the pointer-within collision detector and
    // the live drop indicator. Off by default so legacy consumers are unchanged.
    const isPositionalDropEnabled =
        isRowDragAndDropEnabled && !!rowDragAndDrop?.enablePositionalDrop;
    const headerVariantActionByColumnId = useMemo(
        () =>
            new Map(
                view.columns
                    .map((column) => ({
                        id: column.id,
                        action: config.columns[column.id as TColumn]
                            ?.headerAction,
                    }))
                    .filter(
                        (
                            item,
                        ): item is {
                            id: string;
                            action: ColumnHeaderVariantAction;
                        } => item.action?.type === "variantMenu",
                    )
                    .map((item) => [item.id, item.action]),
            ),
        [config.columns, view.columns],
    );

    const getColumnVariantValue = useCallback(
        (columnId: string, action: ColumnHeaderVariantAction): string => {
            const column = view.columns.find(
                (viewColumn) => viewColumn.id === columnId,
            ) as ViewColumnWithVariantOptions | undefined;
            if (!column) {
                return action.defaultValue;
            }
            return (
                column.options?.customVariants?.[action.optionKey] ??
                // Backward-compat shim: the legacy `options.variant` field was the
                // original storage for "variant" before `customVariants` was introduced.
                // This fallback ensures persisted views written before this change still
                // resolve correctly. `options.variant` is deprecated — new writes go to
                // `customVariants` only.
                (action.optionKey === "variant"
                    ? column.options?.variant
                    : undefined) ??
                action.defaultValue
            );
        },
        [view.columns],
    );

    const handleColumnVariantChange = useCallback(
        ({
            columnId,
            action,
            value,
        }: {
            columnId: string;
            action: ColumnHeaderVariantAction;
            value: string;
        }) => {
            if (!onViewChange) {
                return;
            }

            const updatedColumns = view.columns.map((column) => {
                if (column.id !== columnId) {
                    return column;
                }

                const existingColumn = column as ViewColumnWithVariantOptions;
                const nextCustomVariants = {
                    ...(existingColumn.options?.customVariants ?? {}),
                    [action.optionKey]: value,
                };

                return {
                    ...column,
                    options: {
                        ...existingColumn.options,
                        customVariants: nextCustomVariants,
                        // Backward-compat shim: mirror the value into the legacy
                        // `options.variant` field so old code paths that read it
                        // (e.g. CategoryCell before a view-config reload) still work.
                        ...(action.optionKey === "variant"
                            ? { variant: value }
                            : {}),
                    },
                };
            });

            onViewChange({
                ...view,
                columns: updatedColumns,
            });
        },
        [onViewChange, view],
    );

    const renderHeaderVariantMenu = useCallback(
        (columnId: string) => {
            if (!onViewChange) {
                return null;
            }
            const headerAction = headerVariantActionByColumnId.get(columnId);
            if (!headerAction) {
                return null;
            }

            const selectedValue = getColumnVariantValue(columnId, headerAction);

            if (headerAction.hiddenWhenValues?.includes(selectedValue)) {
                return null;
            }

            return (
                <Menu.Root
                    align="end"
                    trigger={
                        <Button
                            variant="secondary"
                            size="sm"
                            iconOnly
                            aria-label="Column options"
                            onClick={(event) => event.stopPropagation()}
                            className={styles.headerVariantMenuTrigger}
                        >
                            <DotsHorizontalIcon className="icon-color-gray-500" />
                        </Button>
                    }
                >
                    <Menu.RadioGroup
                        value={selectedValue}
                        onValueChange={(value) =>
                            handleColumnVariantChange({
                                columnId,
                                action: headerAction,
                                value,
                            })
                        }
                    >
                        {headerAction.options.map((option) => (
                            <Menu.RadioItem
                                key={option.value}
                                value={option.value}
                            >
                                {option.label}
                            </Menu.RadioItem>
                        ))}
                    </Menu.RadioGroup>
                </Menu.Root>
            );
        },
        [
            getColumnVariantValue,
            handleColumnVariantChange,
            headerVariantActionByColumnId,
            onViewChange,
        ],
    );

    const sortableItems = useMemo(() => {
        const allIds = rows.map((row) => String(row.original.id));
        if (!activeDragRowId || !rowDragAndDrop?.getRowDragGroupId) {
            return allIds;
        }
        const activeRow = rowByIdMap.get(activeDragRowId);
        if (!activeRow) {
            return allIds;
        }
        const activeGroupId = rowDragAndDrop.getRowDragGroupId(activeRow);
        return allIds.filter((id) => {
            const row = rowByIdMap.get(id);
            return (
                row && rowDragAndDrop.getRowDragGroupId!(row) === activeGroupId
            );
        });
    }, [rows, activeDragRowId, rowByIdMap, rowDragAndDrop]);

    // While a drag is active, the ids of every row in the moving set (a
    // multi-selection or a group + descendants), so each gets the lifted
    // treatment — not just the native active row. Null unless the consumer opts
    // in (positional drop + `getActiveDragRowIds`) and the set has >1 member, so
    // single-row drags and legacy tables are unaffected.
    const activeDragSetIds = useMemo(
        () =>
            getActiveDragSetIds({
                isPositionalDropEnabled,
                activeDragRowId,
                getActiveDragRowIds: rowDragAndDrop?.getActiveDragRowIds,
                rowByIdMap,
            }),
        [isPositionalDropEnabled, rowDragAndDrop, activeDragRowId, rowByIdMap],
    );

    const handleRowDragStart = useCallback((event: DragStartEvent) => {
        setActiveDragRowId(String(event.active.id));
    }, []);

    // Resolves the row + position a positional drop should land on from a live
    // drag event. Drives both the live indicator and the committed drop, so what
    // the user sees is exactly what is applied.
    const resolveDropFromEvent = useCallback(
        (event: DragEndEvent | DragMoveEvent): ResolvedRowDrop | null =>
            resolveRowDropFromEvent(event, rowByIdMap, {
                getRowDragGroupId: rowDragAndDrop?.getRowDragGroupId,
                canNestRow: rowDragAndDrop?.canNestRow,
                canDropOnRow: rowDragAndDrop?.canDropOnRow,
            }),
        [rowByIdMap, rowDragAndDrop],
    );

    // Positional only: keep the live indicator tracking the pointer on every
    // move (onDragOver alone only fires when the over-row changes, leaving the
    // indicator stale as the pointer moves within a row).
    const handleRowDragMove = useCallback(
        (event: DragMoveEvent) => {
            const resolved = resolveDropFromEvent(event);
            setDropIndicator((prev) => {
                if (!resolved) {
                    return prev === null ? prev : null;
                }
                const next = buildDropIndicatorState({
                    resolved,
                    activeRow: rowByIdMap.get(String(event.active.id)),
                    overRow: rowByIdMap.get(resolved.rowId),
                    validateDrop: rowDragAndDrop?.validateDrop,
                });
                return prev?.rowId === next.rowId &&
                    prev.position === next.position &&
                    prev.isBlocked === next.isBlocked
                    ? prev
                    : next;
            });
        },
        [resolveDropFromEvent, rowByIdMap, rowDragAndDrop],
    );

    const handleRowDragEnd = useCallback(
        async (event: DragEndEvent) => {
            // Capture the indicator the user last saw before clearing it; the
            // positional commit honours exactly that target (single source of
            // truth) so a drop always lands where the indicator showed.
            const shownIndicator = dropIndicatorRef.current;
            setActiveDragRowId(null);
            setDropIndicator(null);
            if (!isRowDragAndDropEnabled) {
                return;
            }

            const { active } = event;
            const activeId = String(active.id);

            const resolvedTarget = resolveDropCommitTarget({
                isPositionalDropEnabled,
                shownIndicator,
                event,
                rowByIdMap,
                canNestRow: rowDragAndDrop?.canNestRow,
            });
            if (!resolvedTarget || resolvedTarget.rowId === activeId) {
                return;
            }

            const activeRow = rowByIdMap.get(activeId);
            const overRow = rowByIdMap.get(resolvedTarget.rowId);
            if (!activeRow || !overRow) {
                return;
            }

            const activeIndex = rowIdToIndexMap.get(activeId);
            const overIndex = rowIdToIndexMap.get(resolvedTarget.rowId);
            if (activeIndex === undefined || overIndex === undefined) {
                return;
            }

            const getGroupId = rowDragAndDrop?.getRowDragGroupId;
            if (getGroupId && getGroupId(activeRow) !== getGroupId(overRow)) {
                return;
            }

            try {
                await rowDragAndDrop?.onRowDrop({
                    activeRow,
                    overRow,
                    activeIndex,
                    overIndex,
                    dropPosition: resolvedTarget.position,
                });
            } catch {
                // Mutation's onError handles optimistic rollback
            }
        },
        [
            rowByIdMap,
            rowDragAndDrop,
            isRowDragAndDropEnabled,
            isPositionalDropEnabled,
            rowIdToIndexMap,
            dropIndicatorRef,
        ],
    );

    // Overscan: pre-render extra rows above/below the viewport to avoid
    // blank flashes during fast scroll. Use half the viewport-fill estimate
    // (~7-15 rows) to balance smoothness vs DOM size.
    const headerGroupCount = table.getHeaderGroups().length;
    // Read here for the select-all header (see `ViewTableLeafHeaderContext`).
    const { isAllPageRowsSelected, isSomePageRowsSelected } =
        getPageRowSelection({
            table,
            viewOptions: view.options,
            selectAllControl,
        });
    const overscanCount = useMemo(
        () =>
            Math.ceil(
                (initialVisibleRows ??
                    calculateInitialVisibleRows(
                        fixedTopOffsetPx,
                        view.options?.density,
                        headerGroupCount,
                    )) / 2,
            ),
        [
            initialVisibleRows,
            view.options?.density,
            fixedTopOffsetPx,
            headerGroupCount,
        ],
    );

    const densityRowHeight =
        DENSITY_ROW_HEIGHT[view.options?.density ?? ViewDensity.SM];

    // TanStack Virtual — virtualizes the flat rows array.
    // Regular rows use DENSITY_ROW_HEIGHT. Full-width rows are content-sized
    // and measured via measureElement so scroll offsets and the focus
    // indicator stay aligned. getItemKey keeps those measurements attached
    // to the row when indexes shift (expand/collapse).
    const {
        virtualizer: rowVirtualizer,
        virtualItems,
        // Sizes the inner container so the scrollbar reflects the full row count.
        totalSize: totalVirtualSize,
    } = useRowVirtualizer({
        count: rows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => densityRowHeight,
        overscan: overscanCount,
        getItemKey: (index) => rows[index]?.id ?? index,
    });

    // Keep the ref in sync so scrollToFocusedRow can always reach the current instance.
    // Use useLayoutEffect to avoid side effects during the render phase.
    useLayoutEffect(() => {
        rowVirtualizerRef.current = rowVirtualizer;
    }, [rowVirtualizer]);

    // Floating focus indicator: find the virtual item for the focused row so we
    // can position the overlay div. Only defined when the focused row is within
    // the currently rendered virtual window.
    const focusedVirtualItem =
        isKeyboardShortcutsEnabledForTable && focusedRowIndex >= 0
            ? virtualItems.find((v) => v.index === focusedRowIndex)
            : undefined;
    // Keep the ref in sync for imperative scroll-handler updates (avoids re-renders per frame),
    // and place the indicator against the live scroll offset before paint.
    useLayoutEffect(() => {
        focusedVirtualItemRef.current = focusedVirtualItem;
        const indicator = focusedRowIndicatorRef.current;
        const container = tableContainerRef.current;
        if (indicator && container && focusedVirtualItem) {
            indicator.style.top = `${focusedVirtualItem.start - container.scrollTop}px`;
        }
    });
    // Memoize row originals to avoid re-creating on every render
    const keyboardData = useMemo(() => rows.map((row) => row.original), [rows]);

    // Keyboard shortcuts hook
    useViewTableKeyboardShortcuts({
        data: keyboardData,
        focusedRowIndex,
        onFocusNext: handleFocusNext,
        onFocusPrevious: handleFocusPrevious,
        onSelectFocused: handleSelectFocused,
        onDeselectAll: handleDeselectAll,
        onToggleBulkSelectFocused: view.options?.bulkActions
            ? handleToggleBulkSelectFocused
            : noop,
        onToggleBulkSelectAll: view.options?.bulkActions
            ? handleToggleBulkSelectAll
            : noop,
        onShiftSelectNext: view.options?.bulkActions
            ? handleShiftSelectNext
            : noop,
        onShiftSelectPrevious: view.options?.bulkActions
            ? handleShiftSelectPrevious
            : noop,
        onJumpToTop: handleJumpToTop,
        onJumpToBottom: handleJumpToBottom,
        onPreviewFocused: onPreviewFocused ? handlePreviewFocused : undefined,
        enabled: isKeyboardShortcutsEnabledForTable,
        bulkActionsEnabled: !!view.options?.bulkActions,
        isEnterSelectionDisabled,
    });

    // Stabilize callback identity: store frequently-changing function refs
    // so handleRowClick doesn't get a new identity on every render.
    const onRowClickRef = useLatestRef(onRowClick);
    const setRowSelectionRef = useLatestRef(setRowSelection);

    const handleRowClick = useCallback(
        (
            e: ViewTableRowClickEvent,
            row: TData,
            tableRow: ViewTableRow<TData>,
        ) => {
            if (e[BLOCK_EVENT_KEY]) {
                return;
            }

            if (
                e.shiftKey &&
                setRowSelectionRef.current &&
                view.options?.bulkActions
            ) {
                e.preventDefault();
                e.stopPropagation();
                const visibleRows = tableRef.current.getRowModel().rows;
                const clickedIndex = visibleRows.findIndex(
                    (r) => r.id === tableRow.id,
                );
                if (clickedIndex === -1) {
                    return;
                }
                setRowSelectionRef.current((prev: RowSelectionState) =>
                    buildShiftClickSelection({
                        visibleRows,
                        anchorRowId: lastSelectedRowIdRef.current,
                        clickedIndex,
                        prev,
                    }),
                );
                lastSelectedRowIdRef.current = tableRow.id;
                return;
            }

            if (
                (e.metaKey || e.ctrlKey) &&
                setRowSelectionRef.current &&
                view.options?.bulkActions &&
                tableRow.getCanSelect()
            ) {
                e.preventDefault();
                e.stopPropagation();
                const rowId = String(row.id);
                lastSelectedRowIdRef.current = tableRow.id;
                setRowSelectionRef.current((prev: RowSelectionState) => {
                    const next = { ...prev };
                    if (next[rowId]) {
                        delete next[rowId];
                    } else {
                        next[rowId] = true;
                    }
                    return next;
                });
                return;
            }

            if (view.options?.bulkActions) {
                lastSelectedRowIdRef.current = tableRow.id;
            }

            if (!isParentSelectable) {
                if (tableRow.getCanExpand()) {
                    tableRow.toggleExpanded();
                    return;
                }
            }

            if (onRowClickRef.current) {
                // don't call onClick if the clicked element is outside of the table (e.g. a modal element called from the row)
                if (
                    tableContainerRef.current &&
                    !tableContainerRef.current.contains(e.target as Node)
                ) {
                    return;
                }
                // move the keyboard focus to the row
                const clickedIndex = tableRef.current
                    .getRowModel()
                    .rows.findIndex((r) => r.id === tableRow.id);
                setFocusedRowIndex(clickedIndex, "click");

                onRowClickRef.current(row);
            }
        },
        [
            view.options?.bulkActions,
            isParentSelectable,
            setFocusedRowIndex,
            onRowClickRef,
            setRowSelectionRef,
        ],
    );

    // Row props factory
    // Hover styling is handled via CSS :hover pseudo-class (see ViewTable.module.scss)
    // so hover state is NOT tracked in React — avoids re-rendering all rows on mouse movement.
    // Active / focused / bulk-selected state is passed as individual boolean
    // props to each row (computed in the render loop below) so only the 1-2
    // rows whose visual state actually changed re-render — not all 24.
    const getRowProps = useCallback(
        (
            row: ViewTableRow<TData>,
            index: number,
            state: {
                isActive: boolean;
                isFocused: boolean;
                isBulkSelected: boolean;
                isLastVisibleRow: boolean;
                isLastSubRow: boolean;
                isSectionStart: boolean;
            },
        ) => {
            const height =
                DENSITY_ROW_HEIGHT[view.options?.density ?? ViewDensity.SM];

            return {
                className: classNames(
                    "view-table-row",
                    styles.row,
                    getRowClassName?.(row.original),
                    {
                        [styles.clickableRow]:
                            view.options?.selection || row.getCanExpand(),
                        [styles.activeRow]: state.isActive,
                        [styles.focusedRow]: state.isFocused,
                        [styles.bulkSelected]: state.isBulkSelected,
                        [styles.noBorder]: !rowBorders,
                        "view-table-row-top-level": row.depth === 0,
                        [styles.topLevelRow]: row.depth === 0,
                        [styles.subRow]: row.depth > 0,
                        [styles.expandedRow]: row.getIsExpanded(),
                        [styles.lastVisibleRow]: state.isLastVisibleRow,
                        [styles.lastSubRow]: state.isLastSubRow,
                        [styles.sectionStart]: state.isSectionStart,
                    },
                ),
                ["data-row-index"]: index,
                onMouseDown: (e: MouseEvent<HTMLElement>) => {
                    if (e.shiftKey && view.options?.bulkActions) {
                        e.preventDefault();
                    }
                },
                onClick: (e: MouseEvent<HTMLElement>) =>
                    handleRowClick(
                        e as ViewTableRowClickEvent,
                        row.original,
                        row,
                    ),
                style: {
                    height,
                },
            };
        },
        [
            view.options?.selection,
            view.options?.bulkActions,
            view.options?.density,
            rowBorders,
            handleRowClick,
            getRowClassName,
        ],
    );

    const isEmpty = rows.length === 0;
    const isLoading = data === null;

    const { columnPinningMap, pinnedLeftWidth, pinnedRightWidth } = useMemo(
        () => getColumnPinningData(table),
        [table],
    );

    // Pre-build a Map for O(1) persisted-size lookups (avoids O(n) .find() per column)
    const viewSizesByColumnId = useMemo(
        () => new Map(view.columns.map((c) => [c.id, c.options?.size])),
        [view.columns],
    );

    // Which column (if any) is being actively dragged in THIS table right
    // now. We key the "use live local size" branch below off this instead of
    // the sticky userResizedColumnIds — that set never clears on its own, so
    // once a table had been resized it would keep rendering its stale local
    // size even after a sibling table updated the shared view to something else.
    const activelyResizingColumnId =
        table.state.columnResizing.isResizingColumn || null;

    const gridTemplateColumns = useMemo(
        () =>
            getGridTemplateColumns({
                table,
                viewSizesByColumnId,
                userResizedColumnIds,
                activelyResizingColumnId,
            }),
        [
            table,
            viewSizesByColumnId,
            userResizedColumnIds,
            activelyResizingColumnId,
        ],
    );

    return (
        <div
            className={classNames(styles.tableOuter, {
                [styles.hasFixedTopOffsetPx]: fixedTopOffsetPx !== undefined,
            })}
            style={styleWithVars({
                "--fixed-top-offset-px":
                    fixedTopOffsetPx !== undefined
                        ? `${fixedTopOffsetPx}px`
                        : undefined,
            })}
        >
            <div
                className={classNames(styles.tableContainer, className, {
                    [styles.hasFixedTopOffsetPx]:
                        fixedTopOffsetPx !== undefined,
                    [styles.bordered]: bordered,
                    [styles.hideTopBorder]: hideTopBorder,
                    [styles.columnBorders]: columnBorders,
                    [styles.keyboardNavigationEnabled]:
                        isKeyboardShortcutsEnabledForTable,
                    [styles.alwaysShowSelection]:
                        view.options?.alwaysShowSelection,
                })}
                ref={assignTableContainerRef}
                {...{ [VIEW_TABLE_SCROLL_ROOT_ATTRIBUTE]: "" }}
                onMouseEnter={activateKeyboardShortcutsForTable}
                onMouseDownCapture={activateKeyboardShortcutsForTable}
                onFocusCapture={activateKeyboardShortcutsForTable}
                style={styleWithVars({
                    ...style,
                    "--view-table-row-height": `${densityRowHeight}px`,
                    "--view-table-nesting-indent":
                        nestingIndentPx !== undefined
                            ? `${nestingIndentPx}px`
                            : undefined,
                    "--view-table-pinned-left-width":
                        pinnedLeftWidth > 0
                            ? `${pinnedLeftWidth}px`
                            : undefined,
                    "--view-table-pinned-right-width":
                        pinnedRightWidth > 0
                            ? `${pinnedRightWidth}px`
                            : undefined,
                })}
            >
                {prependContent}
                {(() => {
                    const tableContent = (
                        <GridTable
                            noBorder
                            noRowBorder
                            className={classNames(styles.table, {
                                [styles.empty]: isEmpty || isLoading,
                                [styles.hasFooter]: !!footer,
                                [styles.dragInProgress]:
                                    !!activeDragRowId &&
                                    isRowDragAndDropEnabled,
                                [styles.dropNotAllowed]:
                                    isPositionalDropEnabled &&
                                    !!dropIndicator?.isBlocked,
                            })}
                            gridTemplateColumns={gridTemplateColumns}
                        >
                            <GridTableHeader className={styles.headers}>
                                {table
                                    .getHeaderGroups()
                                    .flatMap((headerGroup, depth) =>
                                        headerGroup.headers.map((header) =>
                                            renderViewTableHeaderCell({
                                                header,
                                                depth,
                                                headerGroupCount,
                                                columnPinningMap,
                                                enableColumnResizing,
                                                renderHeaderVariantMenu,
                                                isAllPageRowsSelected,
                                                isSomePageRowsSelected,
                                            }),
                                        ),
                                    )}
                            </GridTableHeader>

                            {/*
                             * Virtualized row rendering via @tanstack/react-virtual.
                             *
                             * Spacer pattern: two full-width <div>s at the top and bottom
                             * of the grid reserve the height of off-screen rows so the
                             * scrollbar accurately reflects the total row count.
                             * This keeps rows in CSS grid flow so subgrid column
                             * alignment is unaffected.
                             *
                             * `getRowModel().rows` is already flat-expanded: when a parent
                             * expands 500 children, those children appear inline in the
                             * array. The virtualizer index directly maps to `rows[i]`.
                             */}
                            {virtualItems.length > 0 && (
                                <div
                                    className={styles.virtualSpacer}
                                    style={{
                                        height: virtualItems[0].start,
                                    }}
                                />
                            )}
                            {isRowDragAndDropEnabled
                                ? virtualItems.map((virtualRow, vi) => {
                                      const row = rows[virtualRow.index];
                                      if (!row) {
                                          return null;
                                      }
                                      const fullWidthRow =
                                          renderFullWidthRowIfPresent({
                                              row,
                                              renderFullWidthRow,
                                              index: virtualRow.index,
                                              measureElement:
                                                  rowVirtualizer.measureElement,
                                          });
                                      if (fullWidthRow) {
                                          return fullWidthRow;
                                      }
                                      const nextRow =
                                          rows[virtualRow.index + 1];
                                      const prevRow =
                                          rows[virtualRow.index - 1];
                                      const rowId = String(row.original.id);
                                      const isDragDisabled = !(
                                          rowDragAndDrop?.canDragRow?.(
                                              row.original,
                                          ) ?? true
                                      );

                                      return (
                                          <SortableViewTableRow
                                              key={row.id}
                                              sortableId={rowId}
                                              isDragDisabled={isDragDisabled}
                                              dragHandleColumnId={
                                                  rowDragAndDrop?.dragHandleColumnId
                                              }
                                              isInActiveDragSet={
                                                  !!activeDragSetIds?.has(rowId)
                                              }
                                              dropIndicatorPosition={
                                                  isPositionalDropEnabled &&
                                                  dropIndicator?.rowId === rowId
                                                      ? dropIndicator.position
                                                      : undefined
                                              }
                                              dropIndicatorBlocked={
                                                  isPositionalDropEnabled &&
                                                  dropIndicator?.rowId ===
                                                      rowId &&
                                                  !!dropIndicator?.isBlocked
                                              }
                                              row={row}
                                              index={virtualRow.index}
                                              columnPinningMap={
                                                  columnPinningMap
                                              }
                                              tableMeta={tableMeta}
                                              getRowProps={getRowProps}
                                              clickable={
                                                  view.options?.selection
                                              }
                                              isActive={
                                                  selectedRowId !== undefined &&
                                                  row.original.id ===
                                                      selectedRowId
                                              }
                                              isFocused={
                                                  isKeyboardShortcutsEnabledForTable &&
                                                  virtualRow.index ===
                                                      focusedRowIndex
                                              }
                                              isBulkSelected={
                                                  !!rowSelection[row.id]
                                              }
                                              {...getRowRenderState({
                                                  table,
                                                  row,
                                              })}
                                              isLastVisibleRow={
                                                  vi === virtualItems.length - 1
                                              }
                                              isLastSubRow={
                                                  row.depth > 0 &&
                                                  (!nextRow ||
                                                      nextRow.depth === 0)
                                              }
                                              isSectionStart={
                                                  row.depth === 0 &&
                                                  !!prevRow &&
                                                  prevRow.depth > 0
                                              }
                                          />
                                      );
                                  })
                                : virtualItems.map((virtualRow, vi) => {
                                      const row = rows[virtualRow.index];
                                      if (!row) {
                                          return null;
                                      }
                                      const fullWidthRow =
                                          renderFullWidthRowIfPresent({
                                              row,
                                              renderFullWidthRow,
                                              index: virtualRow.index,
                                              measureElement:
                                                  rowVirtualizer.measureElement,
                                          });
                                      if (fullWidthRow) {
                                          return fullWidthRow;
                                      }
                                      const nextRow =
                                          rows[virtualRow.index + 1];
                                      const prevRow =
                                          rows[virtualRow.index - 1];

                                      return (
                                          <NonSortableRow
                                              key={row.id}
                                              row={row}
                                              index={virtualRow.index}
                                              columnPinningMap={
                                                  columnPinningMap
                                              }
                                              tableMeta={tableMeta}
                                              getRowProps={getRowProps}
                                              clickable={
                                                  view.options?.selection
                                              }
                                              isActive={
                                                  selectedRowId !== undefined &&
                                                  row.original.id ===
                                                      selectedRowId
                                              }
                                              isFocused={
                                                  isKeyboardShortcutsEnabledForTable &&
                                                  virtualRow.index ===
                                                      focusedRowIndex
                                              }
                                              isBulkSelected={
                                                  !!rowSelection[row.id]
                                              }
                                              {...getRowRenderState({
                                                  table,
                                                  row,
                                              })}
                                              isLastVisibleRow={
                                                  vi === virtualItems.length - 1
                                              }
                                              isLastSubRow={
                                                  row.depth > 0 &&
                                                  (!nextRow ||
                                                      nextRow.depth === 0)
                                              }
                                              isSectionStart={
                                                  row.depth === 0 &&
                                                  !!prevRow &&
                                                  prevRow.depth > 0
                                              }
                                          />
                                      );
                                  })}
                            {virtualItems.length > 0 && (
                                <div
                                    className={styles.virtualSpacer}
                                    style={{
                                        height:
                                            totalVirtualSize -
                                            virtualItems[
                                                virtualItems.length - 1
                                            ].end,
                                    }}
                                />
                            )}
                            {loadMore && (
                                <ViewTableLoadMoreRows
                                    loadMore={loadMore}
                                    table={table}
                                    columnPinningMap={columnPinningMap}
                                    rowHeight={densityRowHeight}
                                />
                            )}
                        </GridTable>
                    );

                    if (isRowDragAndDropEnabled) {
                        return (
                            <DndContext
                                sensors={rowDragSensors}
                                collisionDetection={
                                    isPositionalDropEnabled
                                        ? pointerWithinThenClosestCenter
                                        : closestCenter
                                }
                                onDragStart={handleRowDragStart}
                                onDragMove={
                                    isPositionalDropEnabled
                                        ? handleRowDragMove
                                        : undefined
                                }
                                onDragEnd={handleRowDragEnd}
                                onDragCancel={() => {
                                    setActiveDragRowId(null);
                                    setDropIndicator(null);
                                }}
                            >
                                <SortableContext
                                    items={sortableItems}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {tableContent}
                                </SortableContext>
                            </DndContext>
                        );
                    }

                    return (
                        <RowDragOutProvider
                            rowDragOut={activeRowDragOut}
                            rows={rows}
                            tableId={tableId}
                        >
                            {tableContent}
                        </RowDragOutProvider>
                    );
                })()}
                {(isEmpty || isLoading) && (
                    <div className={styles.blankView}>
                        {isLoading ? loadingView() : emptyView()}
                    </div>
                )}
                {appendContent}
                {footer}
            </div>
            <div
                className={styles.focusedRowIndicatorClip}
                style={{
                    top: `calc(${headerGroupCount} * var(--view-table-header-height, ${HEADER_ROW_PX}px))`,
                }}
            >
                {focusedVirtualItem && (
                    <div
                        ref={focusedRowIndicatorRef}
                        className={styles.focusedRowIndicator}
                        style={{ height: focusedVirtualItem.size + 1 }}
                    />
                )}
            </div>
        </div>
    );
};

export const ViewTable = memo(ViewTableInner) as typeof ViewTableInner;
